'use client'

import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Box, Skeleton, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { normalizeImageUrl } from '../lib/util';
import { logFirebaseEvent } from '../lib/firebase/app';
import OptimizedImage from '../components/OptimizedImage';
import VideoThumbnail from './video_thumbnail';
import VideoModal from './video_modal';

// Lazy-load non-critical components
const EmbeddedTour = lazy(() => import('./embedded_matterport'));
const FullScreenGalleryViewer = lazy(() => import('./full_screen_gallery_viewer'));

interface ImageGalleryProps {
  images: string[];
  title?: string;
  matterportId?: string;
  youVisitUrl?: string;
  altTexts?: Record<string, string>;
  videoUrl?: string;
}

const ImageGalleryWithFeatured: React.FC<ImageGalleryProps> = ({
  images,
  title = '',
  matterportId,
  youVisitUrl,
  altTexts,
  videoUrl
}) => {
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [fullScreenInitialIndex, setFullScreenInitialIndex] = useState(0);
  const [showing3DTour, setShowing3DTour] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<string[]>([]);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const thumbnailsPerRow = 5;
  const featuredImageRef = useRef<HTMLDivElement>(null);

  // Determine if we have a 3D tour and what type
  const has3DTour = Boolean(matterportId || youVisitUrl);
  const tourType = matterportId ? 'matterport' : 'youvisit';
  const tourId = matterportId || youVisitUrl;

  // Track if we have a video
  const hasVideo = Boolean(videoUrl);

  // Get all variants for a normalized URL
  const getAllVariants = (normalizedUrl: string, urls: string[]): string[] => {
    return urls.filter(url => normalizeImageUrl(url) === normalizedUrl);
  };

  // Get unique normalized URLs in current order
  const getUniqueNormalizedUrls = (urls: string[]): string[] => {
    const seen = new Set<string>();
    return urls.map(url => normalizeImageUrl(url))
      .filter(normalizedUrl => {
        if (seen.has(normalizedUrl)) return false;
        seen.add(normalizedUrl);
        return true;
      });
  };

  const normalizedUrls = getUniqueNormalizedUrls(images);
  const uniqueImages = normalizedUrls.map(normalizedUrl => {
    const variants = getAllVariants(normalizedUrl, images);
    return {
      thumbnail: variants.find(url => url.includes('_400x400')) || variants[0],
      featured: variants.find(url => url.includes('_1600x1600')) || variants[0]
    };
  });

  // Function to handle video click
  const handleVideoClick = () => {
    if (videoUrl) {
      setIsVideoModalOpen(true);
    }
  };

  // Adjust featured index handling for video
  useEffect(() => {
    if (hasVideo && featuredIndex === 0) {
      // When on video, don't auto-rotate
      return;
    }

    // Don't set up auto-rotation if showing 3D tour
    if (showing3DTour) return;

    intervalRef.current = setInterval(() => {
      if (!isHovering) {
        const nextIndex = (featuredIndex + 1) % (uniqueImages.length + (hasVideo ? 1 : 0));
        setFeaturedIndex(nextIndex);
      }
    }, 8000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [uniqueImages.length, isHovering, showing3DTour, hasVideo, featuredIndex]);

  // Preload the first 3 images for better user experience
  useEffect(() => {
    if (uniqueImages.length > 0 && preloadedImages.length === 0) {
      // Preload the first 3 images (or fewer if there aren't that many)
      const imagesToPreload = uniqueImages.slice(0, Math.min(3, uniqueImages.length));
      Promise.all(
        imagesToPreload.map((img) => {
          return new Promise<string>((resolve) => {
            const imgElement = new window.Image();
            imgElement.src = img.featured;
            imgElement.onload = () => resolve(img.featured);
            imgElement.onerror = () => resolve(img.featured); // Resolve anyway to avoid blocking
          });
        })
      ).then(loadedImages => {
        setPreloadedImages(loadedImages);
      });
    }
  }, [uniqueImages, preloadedImages]);

  const handlePrevious = () => {
    const totalItems = uniqueImages.length + (hasVideo ? 1 : 0);
    setFeaturedIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
  };

  const handleNext = () => {
    const totalItems = uniqueImages.length + (hasVideo ? 1 : 0);
    setFeaturedIndex((prevIndex) => (prevIndex + 1) % totalItems);
  };

  const handleThumbnailPrevious = () => {
    setThumbnailStartIndex((prevIndex) => Math.max(0, prevIndex - thumbnailsPerRow));
  };

  const handleThumbnailNext = () => {
    const totalItems = uniqueImages.length + (hasVideo ? 1 : 0);
    setThumbnailStartIndex((prevIndex) =>
      Math.min(totalItems - thumbnailsPerRow, prevIndex + thumbnailsPerRow)
    );
  };

  const handleThumbnailClick = (index: number) => {
    setFeaturedIndex(index);
    if (hasVideo && index === 0) {
      setIsVideoModalOpen(true);
    } else {
      // Adjust for gallery viewing - video is not shown in full screen gallery
      const galleryIndex = hasVideo ? index - 1 : index;
      setFullScreenInitialIndex(galleryIndex);
      setIsFullScreenOpen(true);
    }
  };

  const handleTourToggle = () => {
    const newState = !showing3DTour;
    setShowing3DTour(newState);

    // Log the event using Firebase Analytics
    if (newState) {
      logFirebaseEvent('3d_tour_view', {
        tourId,
        tourType,
        timestamp: new Date().toISOString(),
        source: 'image_gallery'
      });
    }
  };

  // Get the featured content based on index
  const renderFeaturedContent = () => {
    if (hasVideo && featuredIndex === 0) {
      return (
        <div
          ref={featuredImageRef}
          className="relative w-full h-[30rem]"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <VideoThumbnail
            videoUrl={videoUrl || ''}
            thumbnailUrl={uniqueImages.length > 0 ? uniqueImages[0].featured : undefined}
            alt={title}
            fill={true}
            onClick={handleVideoClick}
            priority={true}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 92vw, 1200px"
          />
        </div>
      );
    } else {
      // Adjust index for regular images when video exists
      const imageIndex = hasVideo
        ? featuredIndex - 1
        : featuredIndex;

      return uniqueImages[imageIndex] ? (
        <div
          ref={featuredImageRef}
          className="relative w-full h-[30rem]"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <OptimizedImage
            src={uniqueImages[imageIndex].featured}
            alt={altTexts?.[uniqueImages[imageIndex].featured] || `Featured ${title} image ${imageIndex + 1}`}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 92vw, 1200px"
            priority={imageIndex === 0}
            isImportant={imageIndex === 0}
            generateBlurPlaceholder={true}
            loading={imageIndex === 0 ? "eager" : "lazy"}
            className='object-cover rounded-lg shadow-lg transition-shadow duration-300 hover:shadow-xl'
          />
        </div>
      ) : (
        <Skeleton variant='rectangular' width='100%' height='30rem' />
      );
    }
  };

  const totalItems = uniqueImages.length + (hasVideo ? 1 : 0);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Video Modal */}
      {videoUrl && (
        <VideoModal
          videoUrl={videoUrl}
          title={title}
          open={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          autoPlay={true}
        />
      )}

      {has3DTour && (
        <button
          onClick={handleTourToggle}
          className="w-[99%] mb-4 py-2 px-4 ml-1 bg-[#FF385C] text-white rounded-lg flex items-center justify-center gap-2 font-['Inter'] animate-pulse"
        >
          {showing3DTour ? 'Show Images' : 'Show 3D Tour'}
        </button>
      )}
      <Box
        mb={2}
        className='transition-opacity duration-500 ease-in-out h-[30rem] relative'
        style={{ touchAction: showing3DTour ? 'none' : 'auto' }}
      >
        {showing3DTour && has3DTour ? (
          <Suspense fallback={<Skeleton variant="rectangular" width="100%" height="30rem" />}>
            <div className="touch-none">
              <EmbeddedTour
                modelId={tourId}
                tourType={tourType}
                height={tourType === 'youvisit' ? '500px' : '100%'}
              />
            </div>
          </Suspense>
        ) : (
          <>
            {renderFeaturedContent()}
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
              }}
              aria-label="Previous image"
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
              }}
              aria-label="Next image"
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </>
        )}
      </Box>
      <Box
        className='flex-grow relative'
        sx={{ overflowX: 'hidden', width: '100%', height: '120px' }}
      >
        <Box
          className='flex transition-transform duration-300 ease-in-out'
          sx={{
            transform: `translateX(-${thumbnailStartIndex * (100 / thumbnailsPerRow)}%)`,
          }}
        >
          {/* Video thumbnail as first item if available */}
          {hasVideo && (
            <Box
              key="video-thumbnail"
              sx={{
                flexShrink: 0,
                width: `${100 / thumbnailsPerRow}%`,
                padding: '0 4px',
              }}
              onClick={() => handleThumbnailClick(0)}
            >
              <div className="relative w-full h-[100px]">
                <VideoThumbnail
                  videoUrl={videoUrl || ''}
                  thumbnailUrl={uniqueImages.length > 0 ? uniqueImages[0].thumbnail : undefined}
                  alt={title}
                  fill={true}
                  onClick={() => handleThumbnailClick(0)}
                  sizes="(max-width: 768px) 20vw, 10vw"
                />
              </div>
            </Box>
          )}

          {/* Image thumbnails */}
          {uniqueImages.map((image, index) => {
            const thumbnailIndex = hasVideo ? index + 1 : index;
            return (
              <Box
                key={`image-thumbnail-${index}`}
                sx={{
                  flexShrink: 0,
                  width: `${100 / thumbnailsPerRow}%`,
                  padding: '0 4px',
                }}
                onClick={() => handleThumbnailClick(thumbnailIndex)}
              >
                <div className="relative w-full h-[100px]">
                  <OptimizedImage
                    src={image.thumbnail}
                    alt={altTexts?.[image.thumbnail] || `Thumbnail ${thumbnailIndex + 1}`}
                    fill
                    sizes="(max-width: 768px) 20vw, 10vw"
                    className='object-cover rounded-lg cursor-pointer'
                    loading="lazy"
                    quality={60}
                  />
                </div>
              </Box>
            );
          })}
        </Box>

        {totalItems > thumbnailsPerRow && (
          <>
            <IconButton
              onClick={handleThumbnailPrevious}
              disabled={thumbnailStartIndex === 0}
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
                opacity: thumbnailStartIndex === 0 ? 0.3 : 1,
              }}
              aria-label="Previous thumbnails"
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              onClick={handleThumbnailNext}
              disabled={thumbnailStartIndex >= totalItems - thumbnailsPerRow}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
                opacity: thumbnailStartIndex >= totalItems - thumbnailsPerRow ? 0.3 : 1,
              }}
              aria-label="Next thumbnails"
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </>
        )}
      </Box>
      {isFullScreenOpen && (
        <Suspense fallback={<div>Loading gallery...</div>}>
          <FullScreenGalleryViewer
            images={uniqueImages.map(img => img.featured)}
            initialIndex={fullScreenInitialIndex}
            onClose={() => setIsFullScreenOpen(false)}
            title={title}
            altTexts={altTexts}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default ImageGalleryWithFeatured;
